import { useState, useEffect, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Redirect, useHistory } from 'react-router-dom'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as yup from 'yup'
import { ErrorResponse } from '@ocean/api'
import { usePrevious } from '@ocean/hooks'
import { Size } from '@ocean/types/dist/appearance'
import { layouts, common, controls, fields } from '@ocean/ui'
import { ToastUtils, ErrorUtils } from '@ocean/utils'
import { LoginType } from 'types'
import { UserUtils, SsoUtils } from 'utils'
import {
  SiteType,
  MaintenanceInformation,
  MaintenanceStatus,
  SystemInformation,
  SsoSettings,
} from 'core/system/types'
import { AnimatedSection, PreMaintenanceMessage } from 'modules/Auth/components'
import { SsoLoginButton } from 'modules/Auth/components'
import { AuthFormLayout } from 'modules/Auth/layouts'
import { namespace } from 'modules/Auth/localization'
import authRoutes from 'modules/Auth/routing/routes'
import { AuthUserParams, LoginQueryParams } from 'modules/Auth/types'
import { namespace as ssoNamespace } from 'modules/Sso/localization'
import ssoRoutes from 'modules/Sso/routing/routes'
import {
  Header,
  ErrorWrapper,
  Maintenance,
  SpecialMessage,
  Footer,
  SsoLoginButtonWrapper,
  SsoLoginTypography,
} from './LoginForm.styled'

const { Note, Typography, Icon } = common
const { Button, common: commonControls } = controls
const { LoadingLayout } = layouts
const { InputField } = fields
const { ErrorMessage } = commonControls

const I18_PATH = `${namespace}:auth.login`
const I18_PATH_MAINTENANCE = `${namespace}:auth.maintenance`
const I18_PATH_SSO = `${ssoNamespace}:sso`

const INVALID_USER_OR_PASSWORD_ERROR_CODE = 'AuthenticationInvalidUserOrPasswordResponse'

type LoginFormProps = {
  loading?: boolean
  error?: ErrorResponse
  params?: LoginQueryParams
  systemInformation?: SystemInformation
  ssoSettings?: SsoSettings
  ssoProviderIconUrl?: string
  onSubmit: (formData: AuthUserParams) => void
  onRedirectToChangePassword: Callback
}

const LoginForm: React.FC<LoginFormProps> = props => {
  const {
    onSubmit,
    onRedirectToChangePassword,
    loading,
    error,
    params,
    systemInformation,
    ssoSettings,
  } = props
  const prevLoading = usePrevious(loading)
  const [formikBag, setFormikBag] = useState<FormikHelpers<AuthUserParams> | null>(null)
  const { siteType, maintenanceInformation, specialMessage, defaultCustomer } =
    (systemInformation || {}) as SystemInformation
  const {
    maintenanceStatus,
    enablePreMaintenance,
    preMaintenanceMessage,
    maintenanceInProgressMessage,
  } = (maintenanceInformation || {}) as MaintenanceInformation

  const history = useHistory()

  const {
    isSsoEnabled,
    isSsoConfigValid,
    isForcedSsoEnabled,
    ssoProviderName = '',
    ssoProviderIconUrl = '',
  } = ssoSettings || {}

  useEffect(() => {
    if (prevLoading && !loading && formikBag) {
      formikBag.setSubmitting(false)

      if (error)
        switch (error.code) {
          case 'AuthenticationExpiredPasswordResponse':
          case 'AuthenticationPasswordChangeIsForcedResponse':
            onRedirectToChangePassword()
            break
          case 'AuthenticationInvalidUserOrPasswordResponse':
            formikBag.setErrors(ErrorUtils.createHighlightOnlyErrors(['userId', 'password']))
            break
          case 'AuthenticationAccountIsLockedResponse':
            formikBag.setErrors(ErrorUtils.createHighlightOnlyErrors(['userId']))
            break
          case 'AuthenticationCustomerDoesNotExistResponse':
            formikBag.setErrors(ErrorUtils.createHighlightOnlyErrors(['customer']))
            break
        }
    }

    //Display SSO error toast if any error message is set OLPM-174
    const ssoError = SsoUtils.getSsoError()
    if (ssoError) {
      ToastUtils.error(ssoError)
      SsoUtils.unmarkSsoError()
      SsoUtils.unmarkSso()
    }
  }, [formikBag, loading, prevLoading, error, onRedirectToChangePassword])

  const { t } = useTranslation()
  const { company, ecn, ssol } = params || {}
  const companyNameFromUrl = company || (ecn && atob(ecn))
  const showCompanyField = !companyNameFromUrl && (siteType === SiteType.Office || !defaultCustomer)

  const VALIDATION_SCHEMA: yup.SchemaOf<AuthUserParams> = yup.object().shape({
    customer: yup.string().required(t(`${I18_PATH}.companyRequired`)),
    userId: yup.string().required(t(`${I18_PATH}.userIdRequired`)),
    password: yup.string().required(t(`${I18_PATH}.passwordRequired`)),
  })
  // Cache to avoid Formik bug when reinitializing changed initial values
  const initialValues = useMemo(getInitialValues, []) // eslint-disable-line react-hooks/exhaustive-deps

  function getInitialValues() {
    const { loginType, userId, customer } = UserUtils.getUserLoginParams()
    const INITIAL_VALUES: AuthUserParams = {
      customer: '',
      userId: '',
      password: '',
    }

    // Development credentials
    if (process.env.NODE_ENV === 'development') {
      INITIAL_VALUES.customer = 'testdb'
    }

    if (loginType === LoginType.Proxy) {
      INITIAL_VALUES.customer = customer
      INITIAL_VALUES.userId = userId
    }

    if (siteType === SiteType.Vessel) {
      INITIAL_VALUES.customer = defaultCustomer
    }

    if (companyNameFromUrl) {
      INITIAL_VALUES.customer = companyNameFromUrl
    }

    return INITIAL_VALUES
  }

  function handleSubmitForm(values: AuthUserParams, actions: FormikHelpers<AuthUserParams>) {
    onSubmit(values)
    setFormikBag(actions)
  }

  function getSsoLoginRoute() {
    return `${ssoRoutes.login}?ecn=${ecn}`
  }

  function handleSsoRedirect() {
    if (!isSsoConfigValid) {
      ToastUtils.error('Single sign on (SSO) login configuration is incorrect or missing.')
    } else {
      history.push(getSsoLoginRoute())
    }
  }

  function getNormalizedErrorMessage() {
    if (error?.code === INVALID_USER_OR_PASSWORD_ERROR_CODE && siteType === SiteType.Vessel)
      return t(`${I18_PATH}.errors.${INVALID_USER_OR_PASSWORD_ERROR_CODE}`)
    return error?.error
  }

  function renderSpecialMessage() {
    if (!specialMessage) return null
    return (
      <SpecialMessage>
        <Typography variant='h3' dataTest='LoginFormSpecialMessage'>
          {specialMessage}
        </Typography>
      </SpecialMessage>
    )
  }

  function renderDefaultLoginFormSection(isValid: boolean, isSubmitting: boolean) {
    if (ecn && isForcedSsoEnabled && ssol) return null
    return (
      <AuthFormLayout>
        {showCompanyField && (
          <AnimatedSection animationDuration='0.7s'>
            <Field
              title={t(`${I18_PATH}.company`)}
              name='customer'
              icon={<Icon icon='company' />}
              component={InputField}
              size={Size.Large}
            />
          </AnimatedSection>
        )}
        <AnimatedSection animationDuration='0.8s'>
          <Field
            title={t(`${I18_PATH}.user`)}
            name='userId'
            icon={<Icon icon='user' />}
            component={InputField}
            size={Size.Large}
          />
        </AnimatedSection>
        <AnimatedSection animationDuration='0.9s'>
          <Field
            title={t(`${I18_PATH}.password`)}
            name='password'
            type='password'
            icon={<Icon icon='lock' />}
            component={InputField}
            size={Size.Large}
          />
        </AnimatedSection>
        <AnimatedSection animationDuration='1.0s'>
          <Button
            fill
            type='submit'
            title={t(`${I18_PATH}.login`)}
            disabled={!isValid || isSubmitting}
            dataTest='LoginFormSubmitButton'
          />
        </AnimatedSection>
      </AuthFormLayout>
    )
  }

  function renderSsoLoginSection(isSubmitting: boolean) {
    if (!isSsoEnabled || !ecn) return null
    const animationDuration = isForcedSsoEnabled ? '1.0s' : '1.1s'
    const buttonTitle = `${t(`${I18_PATH}.ssoSignInWith`)} ${
      ssoProviderName || t(`${I18_PATH_SSO}.identityProvider`)
    }`
    return (
      <AnimatedSection animationDuration={animationDuration}>
        <SsoLoginButtonWrapper>
          {!isForcedSsoEnabled && (
            <SsoLoginTypography variant='h3' color='white'>
              {t(`${I18_PATH}.or`)}
            </SsoLoginTypography>
          )}
          <SsoLoginButton
            title={buttonTitle}
            disabled={isSubmitting}
            dataTest='LoginFormSsoLoginButton'
            onClick={handleSsoRedirect}
            iconUrl={ssoProviderIconUrl}
          />
        </SsoLoginButtonWrapper>
      </AnimatedSection>
    )
  }

  if (ecn && !ssol && ssoSettings?.isForcedSsoEnabled) {
    return <Redirect to={getSsoLoginRoute()} />
  }

  if (maintenanceStatus === MaintenanceStatus.Enabled)
    return (
      <>
        <Maintenance>
          <Note
            title={t(`${I18_PATH_MAINTENANCE}.currentTitle`)}
            description={maintenanceInProgressMessage}
            dataTest='LoginFormMaintenanceStatusEnabled'
          />
        </Maintenance>
        {renderSpecialMessage()}
      </>
    )

  return (
    <>
      <AnimatedSection animationDuration='0.6s'>
        <Header data-test='LoginFormWelcome'>
          <Typography variant='h3'>{t(`${I18_PATH}.info`)}</Typography>
          <Typography variant='h3'>{t(`${I18_PATH}.start`)}</Typography>
        </Header>
      </AnimatedSection>
      {specialMessage && (
        <AnimatedSection animationDuration='0.675s'>{renderSpecialMessage()}</AnimatedSection>
      )}
      <AnimatedSection animationDuration='0.7s'>
        {error && (
          <ErrorWrapper>
            <ErrorMessage error={getNormalizedErrorMessage()} dataTest='LoginFormErrorMessage' />
          </ErrorWrapper>
        )}
      </AnimatedSection>
      <Formik
        validationSchema={VALIDATION_SCHEMA}
        initialValues={initialValues}
        onSubmit={handleSubmitForm}
        validateOnMount
        validateOnBlur
        validateOnChange
      >
        {({ isValid, isSubmitting }) => (
          <LoadingLayout isLoading={loading}>
            <Form>
              {renderDefaultLoginFormSection(isValid, isSubmitting)}
              {renderSsoLoginSection(isSubmitting)}
            </Form>
          </LoadingLayout>
        )}
      </Formik>
      {maintenanceStatus === MaintenanceStatus.Planned && enablePreMaintenance && (
        <AnimatedSection animationDuration='1.1s'>
          <Maintenance>
            <Note data-test='LoginFormMaintenanceStatusPlanned'>
              <PreMaintenanceMessage text={preMaintenanceMessage} />
            </Note>
          </Maintenance>
        </AnimatedSection>
      )}
      {siteType !== SiteType.Vessel && !isForcedSsoEnabled && (
        <AnimatedSection animationDuration='1.1s'>
          <Footer>
            <Link to={authRoutes.forgot} data-test='LoginFormForgotPassword'>
              <Typography variant='link' color='white' hover='oceanLight'>
                {t(`${I18_PATH}.forgotPassword`)}
              </Typography>
            </Link>
          </Footer>
        </AnimatedSection>
      )}
    </>
  )
}

export default memo(LoginForm)
